import { AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, OnDestroy, signal, ViewChild, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { MarkdownModule } from 'ngx-markdown';
import { ContentStateService } from '../../../content-state.service';
import { Chunk } from '../../chunk.dto';
import { ChunkService } from '../../chunk.service';
import { AuthService } from 'src/app/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { MermaidDialogComponent } from './mermaid-dialog.component';
import { ZoomControlComponent } from 'src/app/shared/components/ui/zoom-control/zoom-control.component';
import { ZoomableDirective } from 'src/app/shared/directives/zoomable.directive';

declare var monaco: any;

@Component({
  selector: 'app-code-chunk-displayer',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, TranslateModule, MonacoEditorModule, MarkdownModule, ZoomControlComponent, ZoomableDirective],
  styleUrls: ['./code-chunk-displayer.scss'],
  template: `
    @if (chunk(); as chunk) {
      <div class="code-card" [class.is-expanded]="isExpanded()">
        <!-- Header -->
        <div class="code-card-header">
          <div class="lang-icon-box" [matTooltip]="languageLabel()" matTooltipPosition="above">
            <mat-icon class="lang-icon">{{ languageIcon() }}</mat-icon>
          </div>

          @if (isEditingTitle()) {
            <div class="edit-title-container" (click)="$event.stopPropagation()">
              <input
                #titleInput
                type="text"
                [value]="tempTitle()"
                (keydown.enter)="saveTitle(titleInput.value)"
                (keydown.escape)="cancelEdit()"
                class="title-edit-input"
              />
              <button mat-icon-button class="edit-action-btn confirm-btn" (click)="saveTitle(titleInput.value)">
                <mat-icon>check</mat-icon>
              </button>
              <button mat-icon-button class="edit-action-btn cancel-btn" (click)="cancelEdit()">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          } @else {
            <span class="code-card-title" [class.editable]="isWriter()" (click)="isWriter() && startEdit($event)">
              {{ chunk.title || ('CreateChunk.Code' | translate) }}
            </span>
          }

          <span class="lang-badge">{{ languageLabel() }}</span>

          <div class="code-card-actions">
            <button mat-icon-button class="copy-btn" (click)="$event.stopPropagation(); copyCode(chunk)" [matTooltip]="'Global.Copy' | translate">
              <mat-icon class="copy-icon">content_copy</mat-icon>
            </button>
            <button mat-icon-button class="open-btn" (click)="$event.stopPropagation(); openEditor(chunk)" [matTooltip]="(isWriter() ? 'Global.Edit' : 'Global.Open') | translate">
              <mat-icon class="open-icon">expand_content</mat-icon>
            </button>
          </div>
        </div>

        <!-- Code Preview -->
        <div class="code-preview-area">
          @if (chunk.mainEdition?.content) {
            @if (chunk.mainEdition?.codeLanguage === 'markdown') {
              <div class="markdown-preview-container custom-scrollbar" [class.expandable]="isExpandable()" [class.expanded]="isExpanded()">
                <markdown [data]="chunk.mainEdition.content"></markdown>
              </div>
            } @else if (chunk.mainEdition?.codeLanguage === 'mermaid') {
              <div
                #zoom="appZoomable"
                appZoomable
                [disableTouchMove]="true"
                [enableWheelZoom]="false"
                [minScale]="0.5"
                [maxScale]="3.0"
                class="mermaid-preview-container"
                [class.grabbing]="zoom.isDragging()"
                [class.expandable]="isExpandable()"
                [class.expanded]="isExpanded()"
              >
                <app-zoom-control
                  class="zoom-toolbar"
                  [(zoomScale)]="zoom.zoomScale"
                  [minScale]="0.5"
                  [maxScale]="3.0"
                  [showFullScreen]="true"
                  (fullscreen)="openFullScreen()"
                  (reset)="zoom.resetZoom()"
                  (click)="$event.stopPropagation()"
                />
                <div
                  #mermaidPreview
                  class="mermaid"
                  [style.transform]="'translate(' + zoom.panX() + 'px, ' + zoom.panY() + 'px) scale(' + zoom.zoomScale() + ')'"
                  [style.transition]="zoom.isDragging() ? 'none' : 'transform 0.2s ease-out'"
                  style="transform-origin: center center;"
                ></div>
              </div>
            } @else {
              <div #codePreview class="code-preview-container custom-scrollbar" [class.expandable]="isExpandable()" [class.expanded]="isExpanded()"></div>
            }
          }

          <!-- Expansion Overlay -->
          @if (isExpandable()) {
            <div class="expansion-overlay">
              <div class="show-more-pill" (click)="$event.stopPropagation(); toggleExpand()">
                <span>{{ (isExpanded() ? 'Global.ShowLess' : 'Global.ShowMore') | translate }}</span>
                <mat-icon class="show-more-icon" [class.rotated]="isExpanded()">expand_more</mat-icon>
              </div>
            </div>
          }
        </div>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CodeChunkDisplayerComponent implements AfterViewInit, OnDestroy {
  chunk = input.required<Chunk>();

  @ViewChild('codePreview', { static: false }) codePreviewEl!: ElementRef<HTMLDivElement>;
  @ViewChild('mermaidPreview', { static: false }) mermaidPreviewEl!: ElementRef<HTMLDivElement>;

  private contentStateService = inject(ContentStateService);
  private chunkService = inject(ChunkService);
  private authService = inject(AuthService);
  private translate = inject(TranslateService);
  private dialog = inject(MatDialog);

  isEditingTitle = signal(false);
  tempTitle = signal('');

  @ViewChild('titleInput') set titleInput(element: ElementRef<HTMLInputElement>) {
    if (element) {
      const input = element.nativeElement;
      input.focus();
      const len = input.value.length;
      input.setSelectionRange(len, len);
    }
  }

  isWriter = computed(() => this.authService.isWriter() && !this.contentStateService.isReader());
  isExpanded = signal(false);

  zoomable = viewChild(ZoomableDirective);

  openFullScreen() {
    const chunkData = this.chunk();
    if (!chunkData?.mainEdition?.content) return;

    this.dialog.open(MermaidDialogComponent, {
      data: {
        content: chunkData.mainEdition.content,
        title: chunkData.title || this.translate.instant('CreateChunk.Code'),
        id: chunkData._id,
      },
      panelClass: 'mermaid-dialog-panel',
      width: '95vw',
      height: '95vh',
      maxWidth: '95vw',
      maxHeight: '95vh',
    });
  }


  isExpandable = computed(() => {
    const codeLang = this.chunk()?.mainEdition?.codeLanguage;
    const content = this.chunk()?.mainEdition?.content || '';
    if (codeLang === 'mermaid') {
      return content.trim().length > 0;
    }
    return content.split('\n').length > 5;
  });

  toggleExpand(): void {
    this.isExpanded.update((v) => !v);
  }

  startEdit(event: MouseEvent): void {
    event.stopPropagation();
    this.tempTitle.set(this.chunk().title || this.translate.instant('CreateChunk.Code'));
    this.isEditingTitle.set(true);
  }

  cancelEdit(): void {
    this.isEditingTitle.set(false);
  }

  saveTitle(newTitle: string): void {
    const trimmedTitle = newTitle.trim();
    if (!trimmedTitle) {
      this.isEditingTitle.set(false);
      return;
    }

    const chunkData = this.chunk();
    if (trimmedTitle === (chunkData.title || this.translate.instant('CreateChunk.Code'))) {
      this.isEditingTitle.set(false);
      return;
    }

    const edition = chunkData.mainEdition || chunkData.editions?.[0];
    if (!edition) {
      this.isEditingTitle.set(false);
      return;
    }

    this.chunkService
      .editChunk({
        id: chunkData._id,
        edition: edition,
        content: edition.content || '',
        contentID: chunkData.contentID || this.contentStateService.contentId,
        language: this.translate.currentLang,
        title: trimmedTitle,
      })
      .subscribe({
        next: () => {
          this.isEditingTitle.set(false);
        },
        error: (err) => {
          console.error('Failed to update chunk title', err);
          this.isEditingTitle.set(false);
        },
      });
  }

  languageLabel = computed(() => {
    const lang = this.chunk()?.mainEdition?.codeLanguage || 'javascript';
    const labels: Record<string, string> = {
      javascript: 'JavaScript',
      python: 'Python',
      clike: 'C++',
      rust: 'Rust',
      css: 'CSS',
      java: 'Java',
      csharp: 'C#',
      php: 'PHP',
      go: 'Go',
      ruby: 'Ruby',
      html: 'HTML',
      vb: 'Visual Basic',
      typescript: 'TypeScript',
      sql: 'SQL',
      bash: 'Bash',
      mermaid: 'Mermaid',
      markdown: 'Markdown',
    };
    return labels[lang] || lang;
  });

  private monacoLangMap: Record<string, string> = {
    javascript: 'javascript',
    typescript: 'typescript',
    python: 'python',
    clike: 'cpp',
    rust: 'rust',
    css: 'css',
    java: 'java',
    csharp: 'csharp',
    php: 'php',
    go: 'go',
    ruby: 'ruby',
    html: 'html',
    vb: 'vb',
    sql: 'sql',
    bash: 'shell',
  };

  languageIcon = computed(() => {
    const lang = this.chunk()?.mainEdition?.codeLanguage || 'javascript';
    switch (lang) {
      case 'html':
        return 'html';
      case 'css':
        return 'css';
      case 'python':
        return 'terminal';
      case 'javascript':
      case 'typescript':
        return 'javascript';
      case 'mermaid':
        return 'schema';
      case 'markdown':
        return 'description';
      default:
        return 'code';
    }
  });

  constructor() {
    this.chunkService.chunkEditEvent$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.colorizePreview();
    });

    effect(() => {
      const content = this.chunk()?.mainEdition?.content;
      const lang = this.chunk()?.mainEdition?.codeLanguage;
      if (content !== undefined) {
        this.zoomable()?.resetZoom();
        this.colorizePreview();
      }
    });
  }

  ngAfterViewInit() {
    this.colorizePreview();
  }

  ngOnDestroy() {}

  private async ensureMonacoLoaded(): Promise<void> {
    if (typeof monaco !== 'undefined' && monaco.editor && monaco.editor.colorize) {
      return;
    }

    const anyWin = window as any;
    if (anyWin.__monacoLoadPromise) {
      return anyWin.__monacoLoadPromise;
    }

    anyWin.__monacoLoadPromise = new Promise<void>((resolve) => {
      const baseUrl = '/assets/monaco/vs';

      const onGotAmdLoader = () => {
        const req = anyWin.require || anyWin.monacoAmdRequire;
        req.config({ paths: { vs: baseUrl } });
        req(['vs/editor/editor.main'], () => {
          resolve();
        });
      };

      if (typeof anyWin.require !== 'undefined' && anyWin.require.config) {
        onGotAmdLoader();
      } else {
        const loaderScript = document.createElement('script');
        loaderScript.type = 'text/javascript';
        loaderScript.src = `${baseUrl}/loader.js`;
        loaderScript.addEventListener('load', () => onGotAmdLoader());
        document.body.appendChild(loaderScript);
      }
    });

    return anyWin.__monacoLoadPromise;
  }

  private async colorizePreview() {
    const content = this.chunk()?.mainEdition?.content || '';
    if (!content) return;

    const codeLang = this.chunk()?.mainEdition?.codeLanguage || 'javascript';

    if (codeLang === 'mermaid') {
      setTimeout(async () => {
        if (!this.mermaidPreviewEl) return;
        const elementId = `mermaid-${this.chunk()._id}`.replace(/[^a-zA-Z0-9-]/g, '-');
        try {
          const mermaid = (await import('mermaid')).default;
          mermaid.initialize({
            startOnLoad: false,
            theme: 'default',
            securityLevel: 'loose',
          });

          const { svg } = await mermaid.render(elementId, content);

          if (this.mermaidPreviewEl) {
            this.mermaidPreviewEl.nativeElement.innerHTML = svg;
          }
        } catch (err) {
          console.error('Mermaid render error:', err);
          const badEl = document.getElementById(elementId) || document.getElementById(`d${elementId}`);
          if (badEl) {
            badEl.remove();
          }
          if (this.mermaidPreviewEl) {
            this.mermaidPreviewEl.nativeElement.innerHTML = `<div style="color: red; padding: 20px; font-size: 14px; border: 1px solid rgba(255,0,0,0.2); border-radius: 8px; background: rgba(255,0,0,0.05);">Error rendering diagram: ${(err as Error).message}</div>`;
          }
        }
      }, 50);
      return;
    }

    if (!this.codePreviewEl) return;

    const monacoLang = this.monacoLangMap[codeLang] || codeLang || 'javascript';

    try {
      await this.ensureMonacoLoaded();
      if (typeof monaco !== 'undefined' && monaco.editor && monaco.editor.colorize) {
        const html = await monaco.editor.colorize(content, monacoLang, {});
        if (this.codePreviewEl) {
          this.codePreviewEl.nativeElement.innerHTML = html;
        }
      } else {
        if (this.codePreviewEl) {
          this.codePreviewEl.nativeElement.innerHTML = `<pre>${this.escapeHtml(content)}</pre>`;
        }
      }
    } catch {
      if (this.codePreviewEl) {
        this.codePreviewEl.nativeElement.innerHTML = `<pre>${this.escapeHtml(content)}</pre>`;
      }
    }
  }

  private escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  openEditor(chunk: Chunk): void {
    this.contentStateService.openCodeEditor({
      contentId: this.contentStateService.contentId,
      chunkId: chunk._id,
      title: chunk.title || 'Code',
      content: chunk.mainEdition?.content || '',
      codeLanguage: chunk.mainEdition?.codeLanguage || 'javascript',
      edition: chunk.mainEdition,
    });
  }

  copyCode(chunk: Chunk): void {
    const content = chunk.mainEdition?.content;
    if (content) {
      navigator.clipboard.writeText(content);
    }
  }
}
